import { AuthData, CSCoreAuthData } from "./authTypes";
import { humps, toObject } from "@civicscience/chops";

import axios from "axios";
import { stringify } from "qs";

export type CreateApiArgs = {
  refreshTokenUrl: string;
  loginUrl: string;
  /**
   * Useful for CORS clients to point to another domain.
   */
  baseUrl?: string;
  /**
   * By default, credentials will NOT be sent on CORS requests.
   * Set this flag to `true` to ensure credentials are sent cross domain.
   */
  withCredentials?: boolean;
};

export const createApi = (apiConfig: CreateApiArgs) => {
  let authData: AuthData | null = null;

  const setAuthData = (data: AuthData) => (authData = data);
  const clearAuthData = () => (authData = null);

  /**
   * Returns the access token (or null) for the given auth data.
   */
  const getAccessToken = async (auth: AuthData | null) => {
    if (auth?.kind === "cs-core") {
      return auth.authData.accessToken;
    }
    if (auth?.kind === "auth0") {
      return auth.fetchAccessToken();
    }

    return null;
  };

  const API = axios.create({
    baseURL: apiConfig.baseUrl,
    withCredentials: apiConfig.withCredentials,

    // TODO: This is necessary, but not sure why the types are off?
    // I need to read the axios docs to better understand
    transformRequest: [
      (data) => humps.decamelizeKeys(data),
      // @ts-ignore
      ...axios.defaults.transformRequest,
    ],
    transformResponse: [
      // @ts-ignore
      ...axios.defaults.transformResponse,
      (data) => humps.camelizeKeys(data),
    ],
    /**
     * See SOF-1926 and https://github.com/CivicScience/core/pull/518 for more discussion.
     * Our Python API expects an array to be serialized to a query string as repeated keys:
     * `tags=brand&tags=retail&tags=news`
     */
    paramsSerializer: (params) =>
      stringify(params, { arrayFormat: "repeat", skipNulls: true }),
  });

  API.interceptors.request.use(
    async (config) => {
      // Edit request config
      const newConfig = { ...config };

      const accessToken = await getAccessToken(authData);
      if (accessToken) {
        newConfig.headers["Authorization"] = `Bearer ${accessToken}`;
      }

      if (config.params) {
        newConfig.params = humps.decamelizeKeys(config.params);
      }

      return newConfig;
    },
    (error) => Promise.reject(error),
  );

  API.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = toObject(error);
      /**
       * If the "login" OR "refresh" endpoint fail, do NOT retry - instead:
       *  1. Clear any existing auth data
       *  2. Return a rejected promise to ensure calling code knows to route to the login page
       */
      if (
        config.url === apiConfig.loginUrl ||
        config.url === apiConfig.refreshTokenUrl
      ) {
        clearAuthData();
        return Promise.reject(error);
      }

      /**
       * If the server responds with a 401:
       *  1. Call the refresh endpoint
       *  2. If it succeeds, retry the failed request
       *  3. If it fails, ensure the user is logged out
       *
       * NOTE: Same as IS2 this will NOT redirect you to the login page if refresh fails.
       * We'll need to think about how to make that work as we need to inform the AuthContext.
       */
      if (response?.status === 401) {
        try {
          const refreshResponse = await axios.get<CSCoreAuthData>(
            apiConfig.refreshTokenUrl,
          );
          setAuthData({
            kind: "cs-core",
            authData: humps.camelizeKeys(refreshResponse.data),
          });
        } catch (e) {
          clearAuthData();
          throw e;
        }

        // at this point, our auth is refreshed
        // allow this call to throw if it encounters an error.
        const retryResponse = await axios.request(config);
        return humps.camelizeKeys(retryResponse.data);
      }

      // TODO: This can be improved.
      // We may just want a callback to allow the consumer to format this.
      // If the error has `detail` bubble that through
      return Promise.reject(response?.data ?? error);
    },
  );

  return {
    API,
    setAuthData,
    clearAuthData,
  };
};
